import React, { useMemo, useState } from 'react';
import { Button, Modal, Progress, Table, Tag } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import { StatusMark, type StatusTone } from '../../../components/StatusMark';
import { useTranslation } from '../../../i18n';
import type { DmCampaignListItem } from '../../../types/dm';

const STATUS_TONE: Record<string, StatusTone> = {
  QUEUED: 'muted',
  SENDING: 'review',
  COMPLETED: 'success',
  PARTIAL_FAILED: 'pending',
  CANCELLED: 'muted'
};

type BroadcastTaskListProps = {
  campaigns: DmCampaignListItem[];
  total: number;
  pageNum: number;
  pageSize: number;
  loading: boolean;
  onPageChange: (page: number) => void;
};

function countText(value?: number | null) {
  return typeof value === 'number' && Number.isFinite(value) ? value.toLocaleString() : '—';
}

function progressPercent(item: DmCampaignListItem) {
  const sent = item.sentCount;
  const total = item.totalRecipients;
  if (typeof sent !== 'number' || typeof total !== 'number' || total <= 0) return 0;
  return Math.min(100, Math.round((sent / total) * 100));
}

function formatTime(value?: string) {
  if (!value) return '—';
  const parsed = dayjs(value);
  return parsed.isValid() ? parsed.format('YYYY-MM-DD HH:mm') : value;
}

function StatusTag({ status }: { status?: string }) {
  const { t } = useTranslation();
  const key = status?.trim() || '';
  const known = key in STATUS_TONE;
  const label = known ? t(`message.campaign_${key}`) : key || '—';
  return <StatusMark tone={STATUS_TONE[key] || 'muted'}>{label}</StatusMark>;
}

function ProductCell({ item }: { item: DmCampaignListItem }) {
  const { t } = useTranslation();
  if (item.showProductCard === false) {
    return <Tag className="m-0 border-0 bg-slate-100 text-slate-400">{t('message.hidden_product')}</Tag>;
  }
  if (item.showProductCard) {
    const title = item.productTitle?.trim();
    const productId = item.productId?.trim();
    const label = title || productId;
    if (!label) return <span className="text-slate-300">—</span>;
    return <Tag className="m-0 max-w-[180px] truncate border-0 bg-emerald-50 text-emerald-600">{label}</Tag>;
  }
  return <span className="text-slate-300">—</span>;
}

function DetailRow({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex gap-4 border-b border-[#f1f3f7] py-3 last:border-b-0">
      <dt className="w-20 shrink-0 text-sm text-slate-400">{label}</dt>
      <dd className="m-0 min-w-0 flex-1 text-sm text-slate-700">{children}</dd>
    </div>
  );
}

export function BroadcastTaskList({
  campaigns,
  total,
  pageNum,
  pageSize,
  loading,
  onPageChange
}: BroadcastTaskListProps) {
  const { t } = useTranslation();
  const [detail, setDetail] = useState<DmCampaignListItem | null>(null);

  const columns: ColumnsType<DmCampaignListItem> = useMemo(
    () => [
      {
        title: t('message.col_task'),
        dataIndex: 'name',
        render: (_, row) => (
          <div>
            <div className="font-medium text-slate-800">{row.name?.trim() || '—'}</div>
            <div className="text-xs text-slate-400">{formatTime(row.ctime)}</div>
          </div>
        )
      },
      {
        title: t('message.col_progress'),
        key: 'progress',
        width: 200,
        render: (_, row) => (
          <div className="w-[160px]">
            <div className="text-sm text-slate-700">
              {countText(row.sentCount)}/{countText(row.totalRecipients)}
            </div>
            <Progress
              percent={progressPercent(row)}
              showInfo={false}
              size="small"
              strokeColor={{ from: '#3b4dd7', to: '#596ae8' }}
              className="!m-0"
            />
          </div>
        )
      },
      {
        title: t('message.col_status'),
        dataIndex: 'status',
        width: 120,
        render: (status?: string) => <StatusTag status={status} />
      },
      {
        title: t('message.col_reply'),
        dataIndex: 'replyRatePercent',
        width: 120,
        render: (rate?: number | null) =>
          typeof rate === 'number' && Number.isFinite(rate) ? (
            <span className="font-medium">{rate}%</span>
          ) : (
            <span className="text-slate-300">—</span>
          )
      },
      {
        title: t('message.col_product'),
        key: 'product',
        width: 200,
        render: (_, row) => <ProductCell item={row} />
      },
      {
        title: t('message.col_actions'),
        key: 'actions',
        width: 100,
        render: (_, row) => (
          <Button type="link" className="wb-text-link" onClick={() => setDetail(row)}>
            {t('chat.view')}
          </Button>
        )
      }
    ],
    [t]
  );

  return (
    <div className="chat-scroll min-h-0 flex-1 overflow-y-auto px-5 py-4">
      <div className="mb-3 flex items-baseline gap-2">
        <span className="text-[15px] font-semibold text-slate-800">{t('message.broadcast')}</span>
        <span className="text-xs text-slate-400">{t('message.task_total', { count: total })}</span>
      </div>
      <Table
        rowKey={(row, index) => (row.id != null ? String(row.id) : `campaign-${index}`)}
        columns={columns}
        dataSource={campaigns}
        loading={loading}
        pagination={{
          current: pageNum,
          pageSize,
          total,
          showSizeChanger: false,
          onChange: onPageChange
        }}
      />
      <p className="mb-0 mt-3 text-xs text-slate-400">{t('message.list_hint')}</p>

      <Modal
        title={detail ? t('message.detail_title', { name: detail.name?.trim() || '—' }) : ''}
        open={Boolean(detail)}
        onCancel={() => setDetail(null)}
        footer={null}
        centered
        width={520}
        closeIcon={
          <span className="cursor-pointer text-sm font-normal text-slate-500 hover:text-slate-700">{t('common.close')}</span>
        }
      >
        {detail ? (
          <dl className="m-0">
            <DetailRow label={t('message.col_status')}>
              <StatusTag status={detail.status} />
            </DetailRow>
            <DetailRow label={t('message.col_progress')}>
              <div className="flex items-center gap-3">
                <Progress
                  percent={progressPercent(detail)}
                  size="small"
                  strokeColor={{ from: '#3b4dd7', to: '#596ae8' }}
                  className="!m-0 flex-1"
                />
                <span className="shrink-0 text-xs text-slate-500">
                  {countText(detail.sentCount)}/{countText(detail.totalRecipients)}
                </span>
              </div>
            </DetailRow>
            <DetailRow label={t('message.success_count')}>{countText(detail.successCount)}</DetailRow>
            <DetailRow label={t('message.fail_count')}>{countText(detail.failCount)}</DetailRow>
            <DetailRow label={t('message.reply_count')}>{countText(detail.replyCount)}</DetailRow>
            <DetailRow label={t('message.reply_rate')}>
              {typeof detail.replyRatePercent === 'number' && Number.isFinite(detail.replyRatePercent)
                ? `${detail.replyRatePercent}%`
                : '—'}
            </DetailRow>
            <DetailRow label={t('message.col_product')}>
              <ProductCell item={detail} />
            </DetailRow>
            <DetailRow label={t('message.created_at')}>{formatTime(detail.ctime)}</DetailRow>
          </dl>
        ) : null}
      </Modal>
    </div>
  );
}
